import { internalAxios } from '@/plugins/vueAxios'
import { defineStore } from 'pinia'
import { translate } from '@/plugins/translate'
import {
  galleryPictureEndpoint as PICTURE_ENDPOINT,
  galleryPictureRedeemEndpoint as PICTURE_UNLOCK_ENDPOINT,
  galleryPictureClaimEndpoint as PICTURE_CLAIM_ENDPOINT,
  galleryPictureAssembleEndpoint as PICTURE_ASSEMBLE_ENDPOINT,
} from '@/globalVariables'
import type { GalleryPicture } from '@/interfaces/gallery/Pictures'
import {
  GalleryPictureCategory,
  GalleryPictureState,
  GalleryPictureUnlockCondition,
} from '@/interfaces/gallery/enums'
import { useGalleryCollectionsStore } from './collectionsStore'
import { formatResult } from '@/helpers/timeFormatter'
import { replacePlaceholder } from '@/plugins/replacePlaceholder'
import { replaceHtmlPlaceholders } from '@/plugins/replaceHtmlPlaceholders'
import { translateDiscipline } from '@/plugins/translateDiscipline'

interface GalleryPacksState {
  picture: GalleryPicture | null
  completedPicture: GalleryPicture | null
}

export const useGalleryPictureStore = defineStore('galleryPicture', {
  state: (): GalleryPacksState => ({
    picture: null,
    completedPicture: null,
  }),

  actions: {
    hideCompletedPicture(): void {
      this.completedPicture = null
    },

    async loadPicture(id: string): Promise<void> {
      if (!id) return

      try {
        this.picture = null

        const data = await internalAxios.get<{}, GalleryPicture>(`${PICTURE_ENDPOINT}${id}`)

        if (data === null) return

        this.picture = data
      } catch (error: unknown) {
        console.error(error)
      }
    },

    async unlockPicture(picture_id: string, updateCollection: boolean = false): Promise<void> {
      if (!picture_id) return

      try {
        const response = await internalAxios.put<{}, GalleryPicture, { picture_id: string }>(
          PICTURE_UNLOCK_ENDPOINT,
          { picture_id },
        )

        if (!response) return

        this.picture = response

        // update current collection with the updated picture data
        if (updateCollection) {
          const collectionsStore = useGalleryCollectionsStore()
          if (this.picture.parameters.category === GalleryPictureCategory.Repeatable)
            collectionsStore.loadRepeatable()
          else if (this.picture.parameters.category === GalleryPictureCategory.Special)
            collectionsStore.loadSpecial()
          else collectionsStore.reloadCollection()
        }
      } catch (error: unknown) {
        console.error(error)
      }
    },

    async claimReward(): Promise<void> {
      if (!this.completedPicture && this.picture?.state !== GalleryPictureState.CompletedUnclaimed)
        return

      try {
        const response = await internalAxios.post<{}, GalleryPicture, { picture_id: string }>(
          PICTURE_CLAIM_ENDPOINT,
          { picture_id: this.completedPicture?.id || this.picture.id },
        )

        if (!response) return

        if (this.picture) this.picture = response
        this.hideCompletedPicture()
      } catch (error: unknown) {
        console.error(error)
      }
    },

    async assemble(position: string | number): Promise<void> {
      if (!this.picture || !position) return

      try {
        const response = await internalAxios.put<
          {},
          GalleryPicture,
          { picture_id: string; position: string | number }
        >(PICTURE_ASSEMBLE_ENDPOINT, { picture_id: this.picture.id, position })

        if (!response) return

        this.picture = response
        if (this.picture.state === GalleryPictureState.CompletedUnclaimed)
          this.handlePictureCompletion()
      } catch (error: unknown) {
        console.error(error)
      }
    },

    handlePictureCompletion(): void {
      this.completedPicture = { ...this.picture }

      // update collection data if the picture is from a collection
      if (this.picture.collection) {
        useGalleryCollectionsStore().checkCollectionAfterPictureComplete(
          this.picture.collection,
          this.picture.id,
        )
      }
    },

    getLockedText(
      unlockCondition: GalleryPictureUnlockCondition | '',
      unlockValue: any,
      discipline: number = 0,
      isPicture: boolean = true,
    ): string {
      if (!unlockCondition) return ''

      if (!isPicture || unlockCondition === GalleryPictureUnlockCondition.Level) {
        return replacePlaceholder(
          replaceHtmlPlaceholders(
            replacePlaceholder(translate('gallery.reachLevel'), '%s', '{b} %s {/b}'),
            'b',
            'text-texts-standard-important',
          ),
          '%s',
          unlockValue,
        )
      }

      if (unlockCondition === GalleryPictureUnlockCondition.PersonalBest) {
        const text = replacePlaceholder(
          translate('gallery.performanceRequirement'),
          '{discipline}',
          translateDiscipline(discipline ?? ''),
        )

        return replacePlaceholder(
          replaceHtmlPlaceholders(
            replacePlaceholder(text, '{performance}', '{b} {performance} {/b}'),
            'b',
            'text-texts-standard-important',
          ),
          '{performance}',
          formatResult(parseFloat(unlockValue), discipline),
        )
      }

      if (unlockCondition === GalleryPictureUnlockCondition.Club) {
        return translate('gallery.joinClub')
      }

      if (
        unlockCondition === GalleryPictureUnlockCondition.Milestone ||
        unlockCondition === GalleryPictureUnlockCondition.MilestonePremium
      ) {
        const text =
          unlockCondition === GalleryPictureUnlockCondition.Milestone
            ? 'gallery.reachIndividualMilestone'
            : 'gallery.reachIndividualPremiumMilestone'

        return replacePlaceholder(
          replaceHtmlPlaceholders(
            replacePlaceholder(translate(text), '%s', '{b} %s {/b}'),
            'b',
            'text-texts-standard-important',
          ),
          '%s',
          unlockValue,
        )
      }

      if (unlockCondition === GalleryPictureUnlockCondition.ClaimChest) {
        return translate('gallery.claimTreasureChest')
      }

      if (unlockCondition === GalleryPictureUnlockCondition.PlayoffAdvance) {
        return replacePlaceholder(
          replaceHtmlPlaceholders(
            replacePlaceholder(
              translate('gallery.advanceToDraw'),
              '{tournament}',
              '{b} {tournament} {/b}',
            ),
            'b',
            'text-texts-standard-important',
          ),
          '{tournament}',
          translate(`event.${unlockValue}`),
        )
      }

      if (unlockCondition === GalleryPictureUnlockCondition.FinishTasks && unlockValue) {
        // text: "Splň všetky úlohy na obtiažnosti %s v príbehu {story}"
        // unlockValue: ['level' => 3, 'story' => 'FrozenStory1']
        const text = replacePlaceholder(
          replaceHtmlPlaceholders(
            replacePlaceholder(translate('gallery.finishChainAtLevel'), '%s', '{b} %s {/b}'),
            'b',
            'text-texts-standard-important',
          ),
          '%s',
          unlockValue.level || 0,
        )
        return replacePlaceholder(
          replaceHtmlPlaceholders(
            replacePlaceholder(text, '{story}', '{b} {story} {/b}'),
            'b',
            'text-texts-standard-important',
          ),
          '{story}',
          translate(`event.${unlockValue.story}`),
        )
      }

      return ''
    },
  },
})
